import { useQuery, useQueryClient } from '@tanstack/react-query'

import { sortProjects } from '@/features/projects/reorder'
import { toProjectSummary } from '@/shared/api/adapt'
import { api } from '@/shared/api/client'
import type { Page, ProjectListItem } from '@/shared/api/contracts'
import { qk } from '@/shared/api/query-keys'
import { ensureUserIndex } from '@/shared/hooks/use-users'

/**
 * El orden global de qué se desarrolla primero.
 *
 * `GET /projects/portfolio` (no `portfolio/projects`, que no existe) devuelve
 * `Page[ProjectListItem]` **ya ordenada por rank**, con el ordinal `position`
 * calculado por el servidor con `ROW_NUMBER()`.
 *
 * **`include_ongoing=true` no es opcional acá.** El default del endpoint es
 * `false`, así que sin el parámetro los permanentes no llegan nunca y la
 * sección "Permanentes" —que existe justamente para mostrarlos sin número— era
 * código inalcanzable: `ongoing` quedaba siempre vacío y `<OngoingSection>`
 * devolvía `null`.
 *
 * `refetchOnWindowFocus: false` no es una optimización: es el antídoto 2 del
 * plan 3.4. Alt-tabbeás al Slack del cliente, volvés, el refetch entra con un
 * drop todavía en vuelo y pisa la cache optimista — la fila salta sola.
 *
 * El orden se aplica UNA vez, al llegar la respuesta: de ahí en más la cache es
 * el orden. Si se reordenara en cada render, el movimiento optimista (que mueve
 * el índice, no la posición) se desharía solo.
 */
export function usePriorityProjects() {
  const client = useQueryClient()

  return useQuery({
    queryKey: qk.portfolio.priorities(),
    queryFn: async () => {
      const [users, page] = await Promise.all([
        ensureUserIndex(client),
        api.get<Page<ProjectListItem>>('projects/portfolio?limit=200&include_ongoing=true'),
      ])
      return sortProjects(page.items.map((item) => toProjectSummary(item, users)))
    },
    refetchOnWindowFocus: false,
  })
}
